import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import alignCenterIcon from '@ckeditor/ckeditor5-core/theme/icons/align-center.svg';
import alignJustifyIcon from '@ckeditor/ckeditor5-core/theme/icons/align-justify.svg';
import alignLeftIcon from '@ckeditor/ckeditor5-core/theme/icons/align-left.svg';
import alignRightIcon from '@ckeditor/ckeditor5-core/theme/icons/align-right.svg';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import horizontalArrowsIcon from '../../../../../styles/fontawesome-5.15.4-web/svgs/solid/arrows-alt-h.svg';
import verticalArrowsIcon from '../../../../../styles/fontawesome-5.15.4-web/svgs/solid/arrows-alt-v.svg';

interface DefaultPosition {
  x: number;
  y: number;
}
interface Props {
  contentType: 'section' | 'ps';
  editor: ClassicEditor;
  params: {
    selection: Selection | null;
    element: HTMLElement | null;
  };
  defaultPosition: DefaultPosition;
  onClose: () => void;
}
interface TextStyleDefinition {
  name: string;
  element: string;
  classes: string[];
}

function ContextMenuPage(props: Props) {
  // Common
  const { contentType, editor, params, defaultPosition, onClose } = props;
  const { t } = useTranslation();
  const editorConfig = editor.config || {};
  const [position, setPosition] = useState({
    x: defaultPosition?.x || 0,
    y: defaultPosition?.y || 0,
  });
  useEffect(() => {
    const menuElement = document.querySelector('.context-menu');
    const menuHeight = menuElement ? menuElement.clientHeight : 0;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const defaultPositionX = Number(defaultPosition?.x || 0);
    const defaultPositionY = Number(defaultPosition?.y || 0);
    const x = defaultPositionX + scrollX;
    let y = defaultPositionY + scrollY - 20;
    // 画面下部から領域外に出たとき
    if (defaultPositionY + menuHeight > window.innerHeight) {
      y = defaultPositionY + scrollY - menuHeight - 20;
      // 上からもウィンドウ外に出たとき（中間あたりに表示）
      if (y - scrollY < 0) {
        y = scrollY + (window.innerHeight - menuHeight) / 2;
      }
    }
    setPosition({ x, y });
  }, [defaultPosition]);

  // 閉じるイベント（初回のみ）
  useEffect(() => {
    window.addEventListener('mousedown', (e) => {
      const menuElement = document.querySelector('.context-menu');
      if (menuElement) {
        // 左クリックで閉じる
        if (e.button === 0) {
          // ただし、メニュー本体の場合はそのまま表示
          if (e.target instanceof HTMLElement) {
            if (menuElement.contains(e.target)) {
              return;
            }
          }
          onClose();
        }
      }
    });
  }, [onClose]);

  // Element
  const element = useMemo(() => params.element, [params.element]);
  // フォーカスしている要素のタイプ： `text` `table` `style`
  const elementType = useMemo(() => {
    if (element) {
      // 直近に.ck-widget__selection-handleが存在してるかどうかでチェック
      const selectionHandle = element.closest('.ck-widget_with-selection-handle');
      if (selectionHandle) {
        return 'table';
      }
      // 今後、ここへタイプを追加できます
    }
    return 'text';
  }, [element]);

  // テキストスタイル
  const textStyleDefinitions: TextStyleDefinition[] = _.get(editorConfig, '_config.style.definitions', []);
  const getTextStyleClassNames = (styleName: string) => {
    const hasCurrentStyle = currentTextStyleNames?.includes(styleName);
    return `label ${hasCurrentStyle ? 'active' : ''}`;
  };
  const handleChangeTextStyle = (event: React.MouseEvent, styleName: string) => {
    event.preventDefault();
    editor.execute('style', { styleName });
    // 念のため、500ms後に閉じる
    setTimeout(() => {
      onClose();
    }, 500);
  };
  const filteredTextStyleDefinitions = useMemo(
    () =>
      textStyleDefinitions.filter((definition) => {
        if (contentType === 'ps') {
          // PSの場合
          const invalidDefinitionNames = [
            // 除外するスタイル名
            t('タイプ別H'),
            t('タイプ別記付'),
          ];
          if (invalidDefinitionNames.includes(definition.name)) {
            return false;
          }
        }
        return true;
      }),
    [textStyleDefinitions, contentType, t]
  );
  const currentTextStyleNames = useMemo(() => {
    if (element) {
      const currentClassNames = element.classList;
      return filteredTextStyleDefinitions
        .filter((definition: TextStyleDefinition) =>
          definition.classes.some((className) => currentClassNames.contains(className))
        )
        .map((definition: TextStyleDefinition) => definition.name);
    }
    return null;
  }, [element, filteredTextStyleDefinitions]);

  // コメント
  interface CommentCategory {
    id: number;
    key: string;
    withText: boolean;
    label: string;
    color: string;
    labelStyle: string;
    commandName: string;
    commandParam: {
      threadId: string;
    };
  }
  const commentCategories = useMemo<CommentCategory[]>(() => _.get(editor, 'commentCategories', []), [editor]);
  const handleAddComment = (event: React.MouseEvent, category: CommentCategory) => {
    event.preventDefault();
    const getNewThreadId = _.get(editor, 'utils.getNewThreadId') as ((id: string) => string) | undefined;
    if (typeof getNewThreadId === 'function') {
      const threadId = getNewThreadId(category.id.toString());
      editor.execute('addCommentThread', { threadId });
      // 念のため、500ms後に閉じる
      setTimeout(() => {
        onClose();
      }, 500);
    }
  };

  // 段落書式（Heading）
  interface HeadingOption {
    model: string;
    view: string;
    title: string;
    class: string;
  }
  const headingOptions = useMemo<HeadingOption[]>(() => _.get(editor, 'config._config.heading.options', []), [editor]);
  const getHeadingClassNames = (model: string) => {
    const isSameModel = currentHeadingModel?.model === model;
    return `label ${isSameModel ? 'active' : ''}`;
  };
  const handleChangeHeading = (event: React.MouseEvent, model: string) => {
    event.preventDefault();
    editor.execute('heading', { value: model });
    // 念のため、500ms後に閉じる
    setTimeout(() => {
      onClose();
    }, 500);
  };
  const filteredHeadingOptions = useMemo(() => {
    const validModelTitles = [t('車両設定マーク'), t('参照ページ')]; // 有効なモデルタイトル
    return headingOptions.filter((option: HeadingOption) => validModelTitles.includes(option.title));
  }, [headingOptions, t]);
  const currentHeadingModel = useMemo(() => {
    if (element) {
      return headingOptions.find((option: HeadingOption) => {
        if (option) {
          const optionView = option.view;
          if (!optionView) {
            return false;
          }
          const searchItem: { tagName: string; classNames: string[] } = {
            tagName: '',
            classNames: [],
          };
          if (typeof optionView === 'string') {
            searchItem.tagName = optionView.toLowerCase();
          } else if (typeof optionView === 'object' && optionView !== null) {
            searchItem.tagName = _.get(optionView, 'name', '').toLowerCase();
            let optionViewClasses = _.get(optionView, 'classes', '') as string | string[];
            if (typeof optionViewClasses === 'string') {
              optionViewClasses = [optionViewClasses];
            }
            searchItem.classNames = optionViewClasses;
          }
          // cloestを使ってチェック
          let selector = `${searchItem.tagName}`;
          if (searchItem.classNames.length > 0) {
            selector += `.${searchItem.classNames.join('.')}`;
          }
          const targetElement = element.closest(selector);
          return !!targetElement;
        }
        return false;
      });
    }
    return null;
  }, [element, headingOptions]);

  // テキスト確定
  const handleChangeFixedText = () => {
    editor.execute('CustomButtonDecisionText:execute');
    // 念のため、500ms後に閉じる
    setTimeout(() => {
      onClose();
    }, 500);
  };
  const isFixedText = useMemo(() => {
    // 直近のps_noパラメータをもつ要素を探し、titleがFixedではじまっているかチェック
    const fixedParent = element?.closest('[ps_no]');
    if (fixedParent) {
      const title = fixedParent.getAttribute('title');
      return title && title.startsWith('Fixed');
    }
    return false;
  }, [element]);
  const fixedTextClassNames = useMemo(() => `label ${isFixedText ? 'active' : ''}`, [isFixedText]);

  /* テーブルエリアのイベント */
  // 列
  const handleInsertColumn = (type: string) => {
    if (type === 'left') editor.execute('insertTableColumnLeft');
    if (type === 'right') editor.execute('insertTableColumnRight');
    onClose();
  };
  const handleDeleteColumn = () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    const result: boolean = confirm('列を削除しますか？');
    if (result) {
      editor.execute('removeTableColumn');
      onClose();
    }
  };

  // 行
  const handleInsertRow = (type: string) => {
    if (type === 'up') editor.execute('insertTableRowAbove');
    if (type === 'down') editor.execute('insertTableRowBelow');
    onClose();
  };
  const handleDeleteRow = () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    const result: boolean = confirm('行を削除しますか？');
    if (result) {
      editor.execute('removeTableRow');
      onClose();
    }
  };

  // セル結合
  const handleMergeCells = (direction: string) => {
    if (direction === 'left') editor.execute('mergeTableCellLeft');
    if (direction === 'right') editor.execute('mergeTableCellRight');
    if (direction === 'down') editor.execute('mergeTableCellDown');
    onClose();
  };

  // セル分離
  const handleSplitCells = (direction: string) => {
    // なぜかckeditorでは分割方向が逆になっているようです
    if (direction === 'horizontal') editor.execute('splitTableCellVertically');
    if (direction === 'vertical') editor.execute('splitTableCellHorizontally');
    onClose();
  };

  // セルの寸法
  const changeCellWidth = (width: string) => {
    const inputElement = document.querySelector('#cell-width-input');
    if (inputElement && inputElement instanceof HTMLInputElement) {
      inputElement.value = width;
      editor.execute('tableCellWidth', {
        value: width,
      });
    }
  };
  const handleCellWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    changeCellWidth(e.target.value);
  };
  const handleQuickWidthChange = (width: number) => {
    changeCellWidth(`${width}%`);
  };
  useEffect(() => {
    const inputElement = document.querySelector('#cell-width-input');
    const cellElement = element?.closest('td, th');
    if (inputElement && cellElement && inputElement instanceof HTMLInputElement) {
      // セルの幅を取得して入力フィールドに設定
      const cellWidth = (cellElement as HTMLElement).style.width || '';
      inputElement.value = cellWidth;
    }
  }, [element]);

  // テーブルセルのテキスト配置
  useEffect(() => {
    const cellElement = element?.closest('td, th');
    let alignment = 'left'; // デフォルトは左寄せ
    if (cellElement && cellElement instanceof HTMLElement) {
      alignment = cellElement.style.textAlign || 'left';
    }
    const buttonElement = document.querySelector(`#table-cell-alignment .ck-button[data-alignment="${alignment}"]`);
    if (buttonElement && buttonElement instanceof HTMLButtonElement) {
      buttonElement.classList.add('ck-on');
    }
  }, [element]);
  const handleTextAlignment = (alignment: string) => {
    const validAlignments = ['left', 'center', 'right', 'justify'];
    if (validAlignments.includes(alignment)) {
      editor.execute('tableCellHorizontalAlignment', { value: alignment });
      onClose();
    }
  };

  // 見出し
  // 見出し：列
  const [hasVerticalHeading, setHasVerticalHeading] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      /* eslint-disable */
      const selection = editor.model.document.selection;
      const position = selection.getFirstPosition();
      /* eslint-enable */
      const tableCell = position?.findAncestor('tableCell');
      if (tableCell) {
        const table = tableCell.findAncestor('table');
        const tdElement = element?.closest('td, th');
        if (element && element instanceof HTMLElement && tdElement && tdElement instanceof HTMLTableCellElement) {
          const headingColumns = Number(table?.getAttribute('headingColumns')) || 0;
          const columnIndex = Number(tdElement.cellIndex);
          setHasVerticalHeading(columnIndex < headingColumns);
        }
      } else {
        setHasVerticalHeading(false);
      }
    }, 100);
  }, [element, editor]);
  // 見出し：行
  const [hasHorizontalHeading, setHasHorizontalHeading] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      /* eslint-disable */
      const selection = editor.model.document.selection;
      const position = selection.getFirstPosition();
      /* eslint-enable */
      const tableCell = position?.findAncestor('tableCell');
      if (tableCell) {
        const table = tableCell.findAncestor('table');
        const trElement = element?.closest('tr');
        if (element && element instanceof HTMLElement && trElement && trElement instanceof HTMLTableRowElement) {
          const headingRows = Number(table?.getAttribute('headingRows')) || 0;
          const rowIndex = Number(trElement.rowIndex);
          setHasHorizontalHeading(rowIndex < headingRows);
        }
      } else {
        setHasHorizontalHeading(false);
      }
    }, 100);
  }, [element, editor]);
  const handleHeadingClick = (type: string) => {
    if (type === 'column') {
      editor.execute('setTableColumnHeader');
    } else if (type === 'row') {
      editor.execute('setTableRowHeader');
    }
    onClose();
  };

  return (
    <div
      className="context-menu"
      style={{
        top: position?.y || 0,
        left: position?.x || 0,
        width: '420px',
      }}
      onMouseDown={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* エリア：テキスト */}
      {elementType === 'text' && (
        <>
          {/* テキスト確定 */}
          {contentType === 'section' && (
            <div>
              <div className="heading">{t('テキスト確定')}</div>
              <div className="body">
                <a href="#fixed-title" className={fixedTextClassNames} onClick={() => handleChangeFixedText()}>
                  {t('テキスト確定')}
                </a>
              </div>
            </div>
          )}
          {/* コメント */}
          {contentType === 'section' && (
            <div>
              <div className="heading">{t('コメント')}</div>
              <div className="body">
                {commentCategories.map((category: CommentCategory) => (
                  <a
                    href={`#${category.key}`}
                    className="comment-label"
                    style={{ color: category.color, border: `1px solid ${category.color}` }}
                    onClick={(e) => handleAddComment(e, category)}
                    key={category.id}
                  >
                    {category.label}
                  </a>
                ))}
              </div>
            </div>
          )}
          {/* テキストスタイル */}
          <div>
            <div className="heading">{t('テキストスタイル')}</div>
            <div className="body">
              {filteredTextStyleDefinitions.map((definition: TextStyleDefinition) => (
                <a
                  href={`#${definition.name}`}
                  className={getTextStyleClassNames(definition.name)}
                  key={definition.name}
                  onMouseDown={(e) => handleChangeTextStyle(e, definition.name)}
                >
                  {definition.name}
                </a>
              ))}
            </div>
          </div>
          {/* 段落書式 */}
          <div>
            <div className="heading">{t('段落書式')}</div>
            <div className="body" style={{ paddingBottom: '0' }}>
              {filteredHeadingOptions.map((option: HeadingOption) => (
                <a
                  href={`#${option.model}`}
                  className={getHeadingClassNames(option.model)}
                  key={option.model}
                  onMouseDown={(e) => handleChangeHeading(e, option.model)}
                >
                  {option.title}
                </a>
              ))}
            </div>
          </div>
        </>
      )}
      {/* エリア：テーブル */}
      {elementType === 'table' && (
        <>
          {/* テキスト確定 */}
          {contentType === 'section' && (
            <div>
              <div className="heading">{t('テキスト確定')}</div>
              <div className="body">
                <a href="#fixed-title" className={fixedTextClassNames} onClick={() => handleChangeFixedText()}>
                  {t('テキスト確定')}
                </a>
              </div>
            </div>
          )}
          {/* 列 */}
          <div>
            <div className="heading">{t('列')}</div>
            <div className="body">
              <a href="#left" className="label" onClick={() => handleInsertColumn('left')}>
                {t('左に列を挿入')}
              </a>
              <a href="#right" className="label" onClick={() => handleInsertColumn('right')}>
                {t('右に列を挿入')}
              </a>
              <a href="#delete" className="label" onClick={() => handleDeleteColumn()}>
                {t('列を削除')}
              </a>
            </div>
          </div>
          {/* 行 */}
          <div>
            <div className="heading">{t('行')}</div>
            <div className="body">
              <a href="#up" className="label" onClick={() => handleInsertRow('up')}>
                {t('上に行を挿入')}
              </a>
              <a href="#down" className="label" onClick={() => handleInsertRow('down')}>
                {t('下に行を挿入')}
              </a>
              <a href="#delete" className="label" onClick={() => handleDeleteRow()}>
                {t('行を削除')}
              </a>
            </div>
          </div>
          {/* セル結合 */}
          <div>
            <div className="heading">{t('結合')}</div>
            <div className="body">
              <a href="#left" className="label" onClick={() => handleMergeCells('left')}>
                {t('左のセルと結合')}
              </a>
              <a href="#right" className="label" onClick={() => handleMergeCells('right')}>
                {t('右のセルと結合')}
              </a>
              <a href="#down" className="label" onClick={() => handleMergeCells('down')}>
                {t('下のセルと結合')}
              </a>
            </div>
          </div>
          {/* セル分離 */}
          <div>
            <div className="heading">{t('分離')}</div>
            <div className="body">
              <a href="#horizontal" className="label" onClick={() => handleSplitCells('horizontal')}>
                {t('横にセルを分離')}
              </a>
              <a href="#vertical" className="label" onClick={() => handleSplitCells('vertical')}>
                {t('縦にセルを分離')}
              </a>
            </div>
          </div>
          {/* セルの寸法 */}
          <div style={{ marginBottom: '17px' }}>
            <div className="heading">{t('寸法')}</div>
            <div className="table-body" style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <input
                id="cell-width-input"
                type="text"
                style={{ width: '150px' }}
                onChange={handleCellWidthChange}
                placeholder={t('セルの幅を入力').toString()}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', opacity: 0.9 }}>
                <a href="#width-25" className="quick-width" onClick={() => handleQuickWidthChange(25)}>
                  25<small>%</small>
                </a>
                <a href="#width-33" className="quick-width" onClick={() => handleQuickWidthChange(33)}>
                  33<small>%</small>
                </a>
                <a href="#width-50" className="quick-width" onClick={() => handleQuickWidthChange(50)}>
                  50<small>%</small>
                </a>
                <a href="#width-66" className="quick-width" onClick={() => handleQuickWidthChange(66)}>
                  66<small>%</small>
                </a>
                <a href="#width-75" className="quick-width" onClick={() => handleQuickWidthChange(75)}>
                  75<small>%</small>
                </a>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '65px',
            }}
          >
            <div style={{ flex: 1 }}>
              {/* 見出し */}
              <div id="table-cell-heading">
                <div className="heading" style={{ marginBottom: '10px' }}>
                  {t('見出し')}
                </div>
                <div>
                  <button
                    className={`ck ck-button ${
                      hasVerticalHeading ? 'ck-on' : 'ck-off'
                    } ck-button_with-text ck-switchbutton`}
                    type="button"
                    onClick={() => handleHeadingClick('column')}
                  >
                    <img src={verticalArrowsIcon} alt={t('縦の矢印').toString()} />
                    <span className="ck ck-button__label">{t('見出し列')}</span>
                    <span className="ck ck-button__toggle">
                      <span className="ck ck-button__toggle__inner" />
                    </span>
                  </button>
                </div>
                <div>
                  <button
                    className={`ck ck-button ${
                      hasHorizontalHeading ? 'ck-on' : 'ck-off'
                    } ck-button_with-text ck-switchbutton`}
                    type="button"
                    onClick={() => handleHeadingClick('row')}
                  >
                    <img src={horizontalArrowsIcon} alt={t('横の矢印').toString()} />
                    <span className="ck ck-button__label">{t('見出し行')}</span>
                    <span className="ck ck-button__toggle">
                      <span className="ck ck-button__toggle__inner" />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              {/* テーブルセルのテキスト配置 */}
              <div id="table-cell-alignment">
                <div className="heading">{t('テキスト配置')}</div>
                <div
                  className="table-body"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div className="ck ck-toolbar ck-toolbar_compact">
                    <div className="ck ck-toolbar__items">
                      <button
                        type="button"
                        className="ck ck-button"
                        data-alignment="left"
                        onClick={() => handleTextAlignment('left')}
                      >
                        <img src={alignLeftIcon} alt={t('左寄せ').toString()} />
                      </button>
                      <button
                        type="button"
                        className="ck ck-button"
                        data-alignment="center"
                        onClick={() => handleTextAlignment('center')}
                      >
                        <img src={alignCenterIcon} alt={t('中央揃え').toString()} />
                      </button>
                      <button
                        type="button"
                        className="ck ck-button"
                        data-alignment="right"
                        onClick={() => handleTextAlignment('right')}
                      >
                        <img src={alignRightIcon} alt={t('右寄せ').toString()} />
                      </button>
                      <button
                        type="button"
                        className="ck ck-button"
                        data-alignment="justify"
                        onClick={() => handleTextAlignment('justify')}
                      >
                        <img src={alignJustifyIcon} alt={t('両端揃え').toString()} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ContextMenuPage;
